import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  CreditCard, Calendar, CheckCircle, AlertCircle, 
  TrendingDown, ChevronRight, Clock, Plus, Trash2,
  Wallet, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  if (!amount) return "₺0,00";
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const Installments = () => {
  const [installments, setInstallments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    merchant: "",
    total_amount: "",
    total_installments: "",
    current_installment: "1",
    start_date: new Date().toISOString().split('T')[0],
    bank_name: "",
    card_last_four: "",
    category: "diger"
  });

  const fetchData = useCallback(async () => {
    try {
      const [instRes, summaryRes] = await Promise.all([
        axios.get(`${API}/installments`, { withCredentials: true }),
        axios.get(`${API}/installments/summary`, { withCredentials: true })
      ]);
      setInstallments(instRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    const totalAmount = parseFloat(formData.total_amount);
    const totalInst = parseInt(formData.total_installments);
    const currentInst = parseInt(formData.current_installment);
    
    if (!totalAmount || !totalInst) {
      toast.error("Tutar ve taksit sayısı gerekli");
      return;
    }

    try {
      await axios.post(`${API}/installments`, {
        description: formData.description,
        merchant: formData.merchant || null,
        total_amount: totalAmount,
        installment_amount: totalAmount / totalInst,
        total_installments: totalInst,
        current_installment: currentInst,
        start_date: formData.start_date,
        bank_name: formData.bank_name || null,
        card_last_four: formData.card_last_four || null,
        category: formData.category
      }, { withCredentials: true });
      
      toast.success("Taksit planı oluşturuldu");
      setShowAddDialog(false);
      setFormData({
        description: "",
        merchant: "",
        total_amount: "",
        total_installments: "",
        current_installment: "1",
        start_date: new Date().toISOString().split('T')[0],
        bank_name: "",
        card_last_four: "",
        category: "diger"
      });
      fetchData();
    } catch (error) {
      toast.error("Taksit oluşturulamadı");
    }
  };

  const handlePayInstallment = async (id) => {
    try {
      await axios.post(`${API}/installments/${id}/pay`, {}, { withCredentials: true });
      toast.success("Taksit ödemesi kaydedildi");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ödeme kaydedilemedi");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu taksit planını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/installments/${id}`, { withCredentials: true });
      toast.success("Taksit planı silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="installments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Taksitler</h1>
          <p className="text-zinc-500 text-sm mt-1">Kredi kartı taksitli alışverişlerinizi takip edin</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-400 hover:bg-amber-300 text-zinc-950" data-testid="add-installment-btn">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Taksit
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-50 font-outfit">Yeni Taksit Planı</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-zinc-400">Açıklama *</Label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Alışveriş açıklaması"
                  className="bg-zinc-950 border-zinc-800 mt-1"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Toplam Tutar *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                    placeholder="0.00"
                    className="bg-zinc-950 border-zinc-800 mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Taksit Sayısı *</Label>
                  <Input 
                    type="number"
                    value={formData.total_installments}
                    onChange={(e) => setFormData({...formData, total_installments: e.target.value})}
                    placeholder="6"
                    className="bg-zinc-950 border-zinc-800 mt-1"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Mevcut Taksit No</Label>
                  <Input 
                    type="number"
                    value={formData.current_installment}
                    onChange={(e) => setFormData({...formData, current_installment: e.target.value})}
                    placeholder="1"
                    className="bg-zinc-950 border-zinc-800 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Başlangıç Tarihi</Label>
                  <Input 
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Banka</Label>
                  <Input 
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    placeholder="Garanti, Yapı Kredi..."
                    className="bg-zinc-950 border-zinc-800 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Kart Son 4 Hane</Label>
                  <Input 
                    value={formData.card_last_four}
                    onChange={(e) => setFormData({...formData, card_last_four: e.target.value})}
                    placeholder="1234"
                    maxLength={4}
                    className="bg-zinc-950 border-zinc-800 mt-1"
                  />
                </div>
              </div>
              {formData.total_amount && formData.total_installments && (
                <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                  <p className="text-xs text-zinc-500">Aylık Taksit Tutarı</p>
                  <p className="text-lg font-mono text-amber-400">
                    {formatCurrency(parseFloat(formData.total_amount) / parseInt(formData.total_installments))}
                  </p>
                </div>
              )}
              <Button type="submit" className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950">
                Oluştur
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-400/10 rounded-sm">
                <CreditCard className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Aktif Taksit</p>
                <p className="text-xl font-mono text-zinc-50">{summary.active_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-400/10 rounded-sm">
                <TrendingDown className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Aylık Ödeme</p>
                <p className="text-xl font-mono text-rose-400">{formatCurrency(summary.total_monthly_payment)}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-400/10 rounded-sm">
                <Wallet className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Kalan Borç</p>
                <p className="text-xl font-mono text-sky-400">{formatCurrency(summary.total_remaining_debt)}</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-400/10 rounded-sm">
                <Calendar className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">Yaklaşan Ödeme</p>
                <p className="text-sm text-zinc-50">
                  {summary.upcoming_payments?.[0]?.date || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Payments */}
      {summary?.upcoming_payments?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <h3 className="text-sm font-medium text-zinc-50 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            Yaklaşan Ödemeler
          </h3>
          <div className="flex flex-wrap gap-2">
            {summary.upcoming_payments.slice(0, 5).map((payment, index) => (
              <div key={index} className="flex items-center gap-2 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm">
                <span className="text-xs text-zinc-500">{payment.date}</span>
                <span className="text-sm text-zinc-50">{payment.description?.slice(0, 20)}</span>
                <span className="text-xs text-amber-400">{payment.installment}</span>
                <span className="text-sm font-mono text-rose-400">{formatCurrency(payment.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Installments List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-lg font-outfit font-medium text-zinc-50">Taksit Planları</h3>
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-zinc-400 hover:text-zinc-50">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <div className="divide-y divide-zinc-800">
          {installments.map((inst, index) => {
            const progress = (inst.paid_installments / inst.total_installments) * 100;
            const isCompleted = inst.status === "completed";
            
            return (
              <motion.div
                key={inst.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 hover:bg-zinc-800/50 transition-colors ${isCompleted ? 'opacity-60' : ''}`}
                data-testid={`installment-${inst.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-sm ${isCompleted ? 'bg-emerald-400/10' : 'bg-amber-400/10'}`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <CreditCard className="w-5 h-5 text-amber-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-zinc-50 font-medium">{inst.description}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                          {inst.bank_name && <span>{inst.bank_name}</span>}
                          {inst.card_last_four && <span>•••• {inst.card_last_four}</span>}
                          <span>|</span>
                          <span>{inst.start_date}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex-1">
                        <Progress value={progress} className="h-2" />
                      </div>
                      <span className="text-sm text-zinc-400 whitespace-nowrap">
                        {inst.paid_installments} / {inst.total_installments} taksit
                      </span>
                    </div>

                    {/* Amount Info */}
                    <div className="mt-3 flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-zinc-500">Toplam: </span>
                        <span className="font-mono text-zinc-50">{formatCurrency(inst.total_amount)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Aylık: </span>
                        <span className="font-mono text-amber-400">{formatCurrency(inst.installment_amount)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Kalan: </span>
                        <span className="font-mono text-rose-400">{formatCurrency(inst.remaining_amount)}</span>
                      </div>
                    </div>

                    {/* Next Payment */}
                    {!isCompleted && inst.next_payment_date && (
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <Calendar className="w-3 h-3 text-zinc-500" />
                        <span className="text-zinc-500">Sonraki ödeme:</span>
                        <span className="text-amber-400">{inst.next_payment_date}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    {!isCompleted && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePayInstallment(inst.id)}
                        className="text-emerald-400 hover:bg-emerald-400/10"
                        data-testid={`pay-installment-${inst.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Öde
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(inst.id)}
                      className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                      data-testid={`delete-installment-${inst.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
          {installments.length === 0 && (
            <div className="p-12 text-center">
              <CreditCard className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">Henüz taksit planı yok</p>
              <p className="text-zinc-600 text-sm mt-1">
                Kredi kartı ekstresi yükleyin veya manuel ekleyin
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Installments;
